import { useCallback, useEffect, useMemo, useState } from "react";
import useAuth from "../useAuth";
import useWebotServer from "../useWebotServer";
import { isWebotServerError } from "../../utils/contacts/contactWriteErrors";
import { clearPersistedCache, readPersistedCache, writePersistedCache } from "../../utils/persistedCache";
import type { CrmMemberPermissions, CrmOrgOverviewResponse, CrmOrgRole } from "../../@types/webotServer";

/**
 * WHAT AM I ALLOWED TO DO IN THIS AGENCY — one fetch per (session, org), shared.
 *
 * The CRM page has to know two things before it can render its own chrome: may I browse
 * the agency pool (`browse`, which decides whether the "מאגר המשרד" tab exists at all),
 * and how much of a colleague's record do I see (`pool`, which decides what a row's
 * expansion shows). Both are one field each on `crmOrgOverview`, which already serves
 * every member their own resolved permissions.
 *
 * ── Why a wrapper and not just `useCrmOrgSettings` ──────────────────────────────
 *
 * That hook is the SETTINGS SCREEN's: it holds optimistic write state, per-control
 * in-flight keys, and it refetches on every mount. This is a read on the critical path of
 * a list page, so it needs the opposite: a module cache, one request per session, and no
 * writes at all. They call the same endpoint and neither owns the other's concerns.
 *
 * ── IT NEVER THROWS, and that is the whole interface ────────────────────────────
 *
 * The endpoint answers 403 for a caller in no agency and 400 for a caller in several who
 * did not name one. Both are NORMAL here — measured 2026-08-16, only 287 of 5,226 users
 * are in any organization at all — so both resolve to `{ inAgency: false }` and the tabs
 * simply do not render. A CRM page that failed because a permission lookup 403'd would be
 * broken for the overwhelming majority of the fleet.
 *
 * A genuine fault (network, 5xx) lands in the same place, deliberately: the honest
 * fallback for "I could not learn your permissions" is the NARROWER answer, which is the
 * page exactly as it was before the agency view existed.
 *
 * ── Keyed by org, because permissions are per agency ───────────────────────────
 *
 * A realtor in two agencies can be an admin of one and a plain member of the other, so a
 * single-keyed cache would answer the wrong question after a tab switch. The caller
 * passes the org it is currently showing (from `useOrgDirectory().orgs`), and `undefined`
 * is its own key — the single-agency case, where the server picks the only candidate.
 */

const STALE_AFTER_MS = 5 * 60 * 1000;

/**
 * ── AND IT SURVIVES A RELOAD ────────────────────────────────────────────────────
 *
 * This is the SECOND of two sequential requests the CRM page's chrome waits on: it cannot
 * be issued until `useOrgDirectory` has produced an org id. So on a hard reload the tab bar
 * was blank for one round trip and then, a second round trip later, learned whether its
 * agency tab was usable — which is when it could finally say so.
 *
 * Persisting the last answer removes both waits: the directory paints from its own
 * persisted copy, this one is keyed on the org id that copy supplies, and both revalidate
 * in the background. A permission changes when an admin edits it, which is not something
 * that happens between two page loads.
 *
 * ⚠️ NOT AN ACCESS DECISION, and it must never become one. The server resolves `browse` on
 * every request from `dashboard-orgs`; what this restores from storage decides whether a
 * TAB looks enabled. A hand-edited value buys a tab that answers 403 when clicked.
 *
 * ⚠️ FAILURES ARE NEVER PERSISTED. In memory a failed lookup is cached as "no agency" on
 * purpose (the narrow answer is the safe one). Written to disk, one transient 5xx would
 * remove the agency view for the whole of the next session.
 */
const PERSIST_KEY = "webot:crmMyAccess:v1";

type Entry = { at: number; data: CrmOrgOverviewResponse | null };

const cache = new Map<string, Entry>();
const inflight = new Map<string, Promise<CrmOrgOverviewResponse | null>>();
/** The account the cache belongs to — a sign-out/sign-in must never inherit it. */
let cachedFor: string | null = null;

/** What goes to storage: the successful answers only, by cache key. */
type PersistedAccess = Record<string, CrmOrgOverviewResponse>;

const persist = (email: string): void => {
  const value: PersistedAccess = {};
  cache.forEach((entry, key) => {
    if (entry.data) value[key] = entry.data;
  });
  writePersistedCache(PERSIST_KEY, email, value);
};

/**
 * Adopt the persisted answers for this account, once.
 *
 * Called from RENDER, not from the effect, and that is the point: the initial state is read
 * out of `cache` during the first render, so hydrating in an effect would paint one frame of
 * "no agency" — the blank bar this exists to remove. It is idempotent (guarded on
 * `cachedFor`) and it owns the account switch that used to live in `load`.
 */
const hydrate = (email: string): void => {
  if (cachedFor === email) return;
  cache.clear();
  inflight.clear();
  cachedFor = email;
  const persisted = readPersistedCache<PersistedAccess>(PERSIST_KEY, email);
  if (!persisted) return;
  Object.entries(persisted.value ?? {}).forEach(([key, data]) => cache.set(key, { at: persisted.at, data }));
};

const normalizeEmail = (value?: string | null): string => (value ?? "").trim().toLowerCase();

const cacheKey = (email: string, orgId?: string): string => `${email}|${orgId ?? ""}`;

/**
 * Drops every cached answer. Call after anything that could change what the caller may do
 * — saving a permission on the settings screen, or a change of agency.
 */
export const invalidateCrmMyAccess = (): void => {
  cache.clear();
  inflight.clear();
  cachedFor = null;
  // ⚠️ THE PERSISTED ANSWERS GO TOO, AND THEY HAVE TO. `hydrate` re-reads storage whenever
  // `cachedFor` is not the current account, so clearing memory alone would restore exactly
  // the answers this call exists to discard — and the caller is usually the settings screen
  // having just CHANGED one of them. It also covers sign-out.
  clearPersistedCache(PERSIST_KEY);
};

export type CrmMyAccess = {
  /** The caller is in an agency AND we know what they may do there. */
  inAgency: boolean;
  orgId: string | null;
  orgName: string | null;
  isAdmin: boolean;
  role: CrmOrgRole | null;
  /**
   * The two fields this hook exists for, plus the rest of the set.
   *
   * `null` while unknown — which is NOT the same as "no permissions". A consumer must
   * render the narrow thing while this is null and widen when it arrives, never the
   * reverse: flashing the agency tab and then removing it is worse than it appearing a
   * beat late.
   */
  permissions: CrmMemberPermissions | null;
  /**
   * May browse the agency pool ("מאגר משרדי"). Decides whether the agency tab is USABLE —
   * the tab itself is rendered either way, disabled and with the reason under it, so this
   * is no longer the gate on whether the feature is visible at all. See `CrmViewTabs`.
   */
  canBrowseOrg: boolean;
  /** Sees anything at all of a colleague's copy — the gate on holders and the expansion. */
  canSeePool: boolean;
  /** Sees a colleague's copy in FULL, rather than name + dates. */
  canSeePoolFull: boolean;
  loading: boolean;
};

const ABSENT: Omit<CrmMyAccess, "loading"> = {
  inAgency: false,
  orgId: null,
  orgName: null,
  isAdmin: false,
  role: null,
  permissions: null,
  canBrowseOrg: false,
  canSeePool: false,
  canSeePoolFull: false
};

/**
 * `enabled: false` skips the request entirely and answers `ABSENT` — the same answer the
 * endpoint gives a caller in no agency, arrived at without asking.
 *
 * It exists for the CONTACT PAGE. This hook now runs there (the ציר זמן tab asks whether the
 * caller may see who else in the agency holds this person), and that page is opened by the
 * whole fleet — of whom only 287 of 5,226 are in any organization at all. For everyone else
 * the request is a guaranteed 403, once per session, on one of the most-visited screens in
 * the product. The org directory already answers "no agencies" from a persisted cache, so
 * the caller can decide not to ask; this is that decision.
 *
 * ⚠️ IT IS NOT AN ACCESS DECISION. Passing `true` grants nothing — the server resolves
 * `browse` from `dashboard-orgs` on every request. Passing `false` only means "I already
 * know there is nothing to ask about", and being wrong about that costs a missing UI
 * affordance, never a leak.
 */
export default function useCrmMyAccess(orgId?: string, options?: { enabled?: boolean }): CrmMyAccess {
  const { user } = useAuth();
  const { crmOrgOverview } = useWebotServer();
  const enabled = options?.enabled !== false;
  const email = normalizeEmail(user?.email);
  const key = cacheKey(email, orgId);

  // Before the first read of `cache` below — see `hydrate`.
  if (email) hydrate(email);

  const warm = email ? cache.get(key) : undefined;
  const [data, setData] = useState<CrmOrgOverviewResponse | null>(warm?.data ?? null);
  const [loading, setLoading] = useState(!warm);

  const load = useCallback(async () => {
    if (!enabled) return;
    if (!email) {
      invalidateCrmMyAccess();
      setData(null);
      setLoading(false);
      return;
    }
    // Idempotent, and the account switch lives inside it.
    hydrate(email);

    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < STALE_AFTER_MS) {
      setData(hit.data);
      setLoading(false);
      return;
    }

    // Concurrent mounts share one request — the workspace and the row menus can both ask
    // on a cold start.
    let request = inflight.get(key);
    if (!request) {
      request = crmOrgOverview(orgId)
        .then((response) => {
          cache.set(key, { at: Date.now(), data: response });
          persist(email);
          return response;
        })
        .catch((error: unknown) => {
          // EVERY failure caches as "no agency", including a 5xx. See the header: the
          // narrow answer is the safe one, and retrying a permission lookup on every
          // render of a list page would be worse than being briefly conservative. The
          // stale window (5 minutes) is the retry.
          if (!isWebotServerError(error)) {
            console.error("useCrmMyAccess: failed to resolve agency permissions", error);
          }
          cache.set(key, { at: Date.now(), data: null });
          return null;
        })
        .finally(() => {
          inflight.delete(key);
        });
      inflight.set(key, request);
    }

    // ⚠️ SHOW THE STALE ANSWER FIRST — same reason as `useOrgDirectory`: the initial state
    // can only be seeded when the account is known on the first render, and auth routinely
    // resolves a tick later. Without this the persisted answer waits for the request it was
    // persisted to avoid waiting for.
    if (hit) setData(hit.data);
    if (!hit) setLoading(true);
    setData(await request);
    setLoading(false);
  }, [crmOrgOverview, email, key, orgId, enabled]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The cached answer counts as the answer, even before state has caught up to it.
   *
   * `data` is seeded from the cache in a state INITIALISER, which runs once — and on a cold
   * load the account is not known yet on that render (auth resolves a tick later). So the
   * render where the email finally arrives has a populated cache and a null `data`, and
   * reading `data` alone would paint one frame of a disabled agency tab before the effect
   * publishes the value sitting right there. Derived rather than pushed into state: no
   * setState during render, no extra effect, and the two can never disagree.
   */
  const resolved = data ?? warm?.data ?? null;

  return useMemo<CrmMyAccess>(() => {
    // Never "loading": nothing was asked, so nothing is pending. A caller that renders a
    // spinner on `loading` must not get one forever because it opted out.
    if (!enabled) return { ...ABSENT, loading: false };
    if (!resolved) return { ...ABSENT, loading };
    const permissions = resolved.permissions;
    return {
      inAgency: true,
      orgId: resolved.orgId ?? null,
      orgName: resolved.orgName ?? null,
      isAdmin: Boolean(resolved.isAdmin),
      role: resolved.role ?? null,
      permissions,
      canBrowseOrg: Boolean(permissions?.browse),
      canSeePool: permissions?.pool !== "none",
      canSeePoolFull: permissions?.pool === "full",
      // Known is known: a cached answer on screen is not "loading", whatever the request
      // behind it is doing. (The `!resolved` case returns above, with the real flag.)
      loading: false
    };
  }, [resolved, loading, enabled]);
}
